import React, { useMemo } from "react"

import ReservationStatusModal from "@/components/public/reservation_status_modal"
import MobileBookingBottomSheets from "@/components/public/venue/mobile_booking_bottom_sheets"
import ReservationModal from "@/components/public/venue/reservation_modal"
import i18n from "@/i18n"
import reservationStatusModalIcon from "@/pages/public/reservation_status_modal_icon.svg"
import {
  buildBookingPolicyTiles,
  parseBookingPolicyTileKinds,
  buildBookingPolicySectionFromRules,
} from "@/shared/booking_policy"

import type { ModalState, ModalAction, VenueType } from "./types"

type Props = {
  venue: VenueType
  venueLogoUrl: string | null
  modalState: ModalState
  dispatch: React.Dispatch<ModalAction>
}

const BookingFlow: React.FC<Props> = ({ venue, venueLogoUrl, modalState, dispatch }) => {
  const availableBookingPolicyTiles = useMemo(
    () => parseBookingPolicyTileKinds(venue.reservationConfiguration?.availableBookingPolicyTiles),
    [venue.reservationConfiguration?.availableBookingPolicyTiles],
  )
  const bookingPolicyTilesConfig = useMemo(
    () => parseBookingPolicyTileKinds(venue.reservationConfiguration?.bookingPolicyTiles),
    [venue.reservationConfiguration?.bookingPolicyTiles],
  )

  const bookingPolicyTiles = useMemo(
    () =>
      buildBookingPolicyTiles(
        {
          availableBookingPolicyTiles,
          bookingPolicyTiles: bookingPolicyTilesConfig,
          minReservationTime: venue.reservationConfiguration?.minReservationTime,
          maxReservationTime: venue.reservationConfiguration?.maxReservationTime,
          noshowAutoCancelAfter: venue.reservationConfiguration?.noshowAutoCancelAfter,
          largeGroupThreshold: venue.reservationConfiguration?.largeGroupThreshold,
          cancellationDeadline: venue.reservationConfiguration?.cancellationDeadline,
          phone: venue.info?.phone,
          email: venue.info?.email,
        },
        { includeDisabled: false, includeUnconfigured: false },
      ),
    [availableBookingPolicyTiles, bookingPolicyTilesConfig, venue.reservationConfiguration, venue.info],
  )

  const bookingPolicySection = useMemo(
    () => buildBookingPolicySectionFromRules(venue.reservationRuleSections),
    [venue.reservationRuleSections],
  )

  return (
    <>
      <MobileBookingBottomSheets
        open={modalState.mobileBookingOpen}
        onClose={() => dispatch({ type: "CLOSE_MOBILE_BOOKING" })}
        onReserveClick={() => dispatch({ type: "OPEN_FROM_MOBILE_BOOKING" })}
        timezone={venue.timezone}
        country={venue.country}
        openingHours={venue.openingHours || []}
        minAdvanceBookingTime={venue.reservationConfiguration?.minAdvanceBookingTime}
        minReservationTime={venue.reservationConfiguration?.minReservationTime}
        maxReservationTime={venue.reservationConfiguration?.maxReservationTime}
        tables={venue.tables}
      />

      <ReservationModal
        open={modalState.bookingOpen}
        onClose={() => dispatch({ type: "CLOSE_BOOKING" })}
        onMobileBack={() => dispatch({ type: "BACK_TO_MOBILE_BOOKING" })}
        venueSlug={venue.slug}
        venueName={venue.name}
        venueLogoUrl={venueLogoUrl}
        venuePlans={venue.plans}
        address={venue.info?.address}
        city={venue.info?.city}
        tables={venue.tables}
        openingHours={venue.openingHours || []}
        timezone={venue.timezone}
        country={venue.country}
        minAdvanceBookingTime={venue.reservationConfiguration?.minAdvanceBookingTime}
        minReservationTime={venue.reservationConfiguration?.minReservationTime}
        maxReservationTime={venue.reservationConfiguration?.maxReservationTime}
        holdDurationSeconds={venue.reservationConfiguration?.noshowAutoCancelAfter}
        mobileSheet={modalState.mobileMode}
        bookingPolicyTiles={bookingPolicyTiles}
        bookingPolicySection={bookingPolicySection}
      />

      <ReservationStatusModal
        open={modalState.confirmation !== null}
        onClose={() => dispatch({ type: "DISMISS_CONFIRMATION" })}
        icon={reservationStatusModalIcon}
        title={i18n.t(
          modalState.confirmation === "invalid"
            ? "app.public.venue.modal.invalid_confirmation_link"
            : "app.public.venue.modal.reservation_cancelled",
        )}
        description={i18n.t(
          modalState.confirmation === "invalid"
            ? "app.public.venue.modal.invalid_confirmation_description"
            : "app.public.venue.modal.expired_confirmation_description",
        )}
        primaryAction={{
          label: i18n.t("app.public.venue.modal.make_a_new_reservation"),
          onClick: () => dispatch({ type: "DISMISS_CONFIRMATION" }),
        }}
      />
    </>
  )
}

export default BookingFlow
